import {GenericDetails, RedirectPaymentDetails, ThreeDSPaymentDetails} from './../domain/entities/PaymentDetails';
import {Injectable} from "@angular/core";
import {CredentialsStorage} from "@app/core/credentials/domain/credentials-storage";
import {DeliveryPointLocal} from "@app/core/delivery-point/domain/delivery-point.local";
import {ApiUrls} from "@app/core/shared/infrastructure/api-urls";
import {HandlerBuilder, HttpMethod} from "@app/core/shared/infrastructure/base.handler/base-handle.parameters";
import {EuropastryBaseHandler} from "@app/core/shared/infrastructure/base.handler/europastry-base.handler";
import {BaseHandleErrorMapper} from "@app/core/shared/infrastructure/base.handler/base-handle-error.mapper";
import {Observable} from "rxjs";
import {catchError, map, switchMap, take} from "rxjs/operators";
import {MyOrdersListPage} from "../domain/dtos/my-orders-list-page";
import {OrderRemote} from "../domain/repository/order.remote";
import {OrderListPageMapper} from "./mappers/order-list-page.mapper";
import {MyOrderDetails} from "../domain/dtos/my-order-details";
import {OrderDetailsMapper} from "./mappers/order-details.mapper";
import {MyOrderSorting} from "../domain/dtos/my-order-sorting.enum";
import {DropInResponse} from "@app/core/my-order/domain/dtos/drop-in-response";
import {PayOrderResponse} from "@app/core/my-order/domain/dtos/pay-order-response";
import {PaymentDetails} from '../domain/entities/PaymentDetails';
import {getAdyenChannel} from "@app/core/shared/infrastructure/adyen-channel";


@Injectable({
    providedIn: "root",
})
export class OrderWebService implements OrderRemote {

    private ADYEN_CHANNEL =  getAdyenChannel();

    constructor(
        private baseHandler: EuropastryBaseHandler,
        private credentialsStorage: CredentialsStorage,
        private deliveryPointLocal: DeliveryPointLocal,
        private apiUrls: ApiUrls,
    ) { }

    public getOrderList(itemsPerPage: number, page: number, sorting: MyOrderSorting): Observable<MyOrdersListPage> {
        return this.deliveryPointLocal.currentDeliveryPointCode$.pipe(switchMap(deliveryPointCode => {

            const parameters = new HandlerBuilder()
                .method(HttpMethod.get)
                .url(this.apiUrls.ORDER_LIST + sorting + '/DESC/' + deliveryPointCode + '/' + itemsPerPage + '/' + page)
                .authorization(this.credentialsStorage.getCredentials())
                .build()

            return this.baseHandler.execute(parameters).pipe(
                map(OrderListPageMapper.deserialize),
                catchError(BaseHandleErrorMapper.handleError)
            );
        }));
    }

    public getOrderDetail(orderNumber: string): Observable<MyOrderDetails> {
        return this.deliveryPointLocal.currentDeliveryPointCode$.pipe(switchMap(deliveryPointCode => {
            const parameters = new HandlerBuilder()
                .method(HttpMethod.get)
                .url(this.apiUrls.ORDER_DETAIL + orderNumber + '/' + deliveryPointCode)
                .authorization(this.credentialsStorage.getCredentials())
                .build()

            return this.baseHandler.execute(parameters).pipe(
                map(response => OrderDetailsMapper.deserialize(response.results)),
                catchError(BaseHandleErrorMapper.handleError)
            );
        }));
    }

    public getOrderReceipt(orderNumber: string): Observable<undefined> {
        return this.deliveryPointLocal.currentDeliveryPointCode$.pipe(switchMap(deliveryPointCode => {
            const parameters = new HandlerBuilder()
                .method(HttpMethod.get)
                .url(this.apiUrls.ORDER_RECIPT + deliveryPointCode + '/' + orderNumber)
                .authorization(this.credentialsStorage.getCredentials())
                .build()

            return this.baseHandler.execute(parameters).pipe(
                catchError(BaseHandleErrorMapper.handleError)
            );
        }));
    }

    private transformPaymentMethod(dropInPaymentMethod: DropInResponse['paymentMethod']): any {
        const TYPE_GOOGLE_PAY = 'googlepay';
        const TYPE_APPLE_PAY = 'applepay';
        const isGooglePay = dropInPaymentMethod.type === TYPE_GOOGLE_PAY;
        const isApplePay = dropInPaymentMethod.type === TYPE_APPLE_PAY;

        if (isGooglePay) {
            return {
                type: dropInPaymentMethod.type,
                token: dropInPaymentMethod.googlePayToken,
            }
        }

        if (isApplePay) {
            return {
                type: dropInPaymentMethod.type,
                token: dropInPaymentMethod.token,
            }
        }

        const isNewPaymentMethod = dropInPaymentMethod.storedPaymentMethodId == null;

        const paymentMethod = {
            storedPaymentMethodId: dropInPaymentMethod.storedPaymentMethodId,
            encryptedSecurityCode: dropInPaymentMethod.encryptedSecurityCode,
            type: dropInPaymentMethod.type,
        }

        const newPaymentMethod = {
            encryptedCardNumber: dropInPaymentMethod.encryptedCardNumber,
            encryptedExpiryMonth: dropInPaymentMethod.encryptedExpiryMonth,
            encryptedExpiryYear: dropInPaymentMethod.encryptedExpiryYear,
            encryptedSecurityCode: dropInPaymentMethod.encryptedSecurityCode,
            type: dropInPaymentMethod.type,
        }

        return isNewPaymentMethod ? newPaymentMethod : paymentMethod;
    }

    public payOrder(orderCode: number, dropInResponse: DropInResponse): Observable<PayOrderResponse> {

        const paymentMethod = this.transformPaymentMethod(dropInResponse.paymentMethod);

        return this.deliveryPointLocal.currentDeliveryPointCode$.pipe(
            take(1),
            switchMap(deliveryPointCode => {
                const parameters = new HandlerBuilder()
                    .method(HttpMethod.post)
                    .url(this.apiUrls.PAY_ORDER(deliveryPointCode, orderCode))
                    .authorization(this.credentialsStorage.getCredentials())
                    .header(new Map().set('channel', this.ADYEN_CHANNEL))
                    .body({
                        paymentMethod: paymentMethod,
                        browserInfo: dropInResponse.browserInfo,
                    })
                    .build();

                return this.baseHandler.execute(parameters).pipe(
                    catchError(BaseHandleErrorMapper.handleError),
                );
            }));
    }

    public submitAdditionalPaymentDetails(orderCode: number, paymentDetails: PaymentDetails): Observable<PayOrderResponse> {
        let requestBody = {};

        if (paymentDetails instanceof RedirectPaymentDetails) {
            requestBody = { 
                details: {
                    redirectResult: paymentDetails.result,
                } 
            }
        }
       
        if (paymentDetails instanceof ThreeDSPaymentDetails) {
            requestBody = { 
                details: {
                    threeDSResult: paymentDetails.result,
                } 
            }
        }

        if (paymentDetails instanceof GenericDetails) {
            requestBody = {
                details: paymentDetails.result,
            }
        }
            

        return this.deliveryPointLocal.currentDeliveryPointCode$.pipe(
            take(1),
            switchMap(deliveryPointCode => {
                const parameters = new HandlerBuilder()
                    .method(HttpMethod.post)
                    .url(this.apiUrls.SUBMIT_ADDITIONAL_PAYMENT_DETAILS(deliveryPointCode, orderCode))
                    .authorization(this.credentialsStorage.getCredentials())
                    .header(new Map().set('channel', this.ADYEN_CHANNEL))
                    .body(requestBody)
                    .build();

                return this.baseHandler.execute(parameters).pipe(
                    catchError(BaseHandleErrorMapper.handleError),
                );
            }),
        )
    }

}
